import React, { memo, useEffect, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useGetSlotSegmentsQuery } from '../../../redux/globalOverview';
import SplitShiftSegmentsTable from './SplitShiftSegmentsTable';

import './SplitShiftDetailsOverlay.scss';

const SplitShiftDetailsOverlay = ({ slotId, managementCompanyId, triggerClassName, segmentsCount, children }) => {
	const overlayPanelRef = useRef(null);
	const hideTimeoutRef = useRef(null);
	const triggerRef = useRef(null);
	const [hasHovered, setHasHovered] = useState(false);

	const { data: segments = [], isFetching, refetch } = useGetSlotSegmentsQuery(
		{ slotId, managementCompanyId },
		{ skip: !hasHovered, refetchOnMountOrArgChange: true }
	);

	// The split was reverted after this row was rendered, so there is nothing to show.
	const hasNoSegments = hasHovered && !isFetching && segments.length === 0;

	const cancelHide = () => {
		if (hideTimeoutRef.current) {
			clearTimeout(hideTimeoutRef.current);
			hideTimeoutRef.current = null;
		}
	};

	const scheduleHide = () => {
		cancelHide();
		hideTimeoutRef.current = setTimeout(() => {
			if (overlayPanelRef.current) {
				overlayPanelRef.current.hide();
			}
		}, 150);
	};

	const handleTriggerMouseEnter = (event) => {
		cancelHide();
		// This overlay stays mounted with the row, so re-hovering starts no new subscription and
		// refetchOnMountOrArgChange only covers the first hover. Refetch so a split edited elsewhere shows up.
		if (hasHovered) {
			refetch();
		}
		setHasHovered(true);
		triggerRef.current = event.currentTarget;
		if (overlayPanelRef.current) {
			overlayPanelRef.current.show(event, event.currentTarget);
		}
	};

	useEffect(
		() => () => {
			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}
		},
		[]
	);

	return (
		<>
			<span className={triggerClassName} onMouseEnter={handleTriggerMouseEnter} onMouseLeave={scheduleHide}>
				{children}
			</span>
			<OverlayPanel ref={overlayPanelRef} className="split-shift-details-overlay">
				<div onMouseEnter={cancelHide} onMouseLeave={scheduleHide}>
					<div className="split-shift-details-overlay__title">Split shift details</div>
					<SplitShiftSegmentsTable
						segments={segments}
						isFetching={isFetching}
						segmentsCount={segmentsCount}
						hasNoSegments={hasNoSegments}
					/>
				</div>
			</OverlayPanel>
		</>
	);
};

export default memo(SplitShiftDetailsOverlay);
